import React, {useState, useEffect} from "react"
import {Button, Card, Form, Input, Segmented, Alert, Typography, Spin} from "antd"
import {t} from "@lingui/core/macro"
import {Trans} from "@lingui/react/macro"

import {AccountAPI, setToken, buildRegistrationFromSubmit} from "./api"
import AccountEditorRender from "./AccountEditorRender"

const {Text} = Typography

// Connexion / inscription passwordless (email + code OTP).
//
//   mode "login"  : email seul → request-code → écran code → verify-code.
//   mode "signup" : formulaire d'inscription DYNAMIQUE rendu par FormEditorRender
//                   (module identity + bloc complémentaire éventuel, construit côté
//                   API). À la soumission, on extrait l'email + le `registration`
//                   (identité/adresse/extras au format natif), on envoie le code,
//                   puis l'écran code finalise via verify-code.
export default function AuthScreen({identifier, org, lang, onAuthenticated}) {
    const [mode, setMode] = useState("login")      // login | signup
    const [step, setStep] = useState("form")        // form | code
    const [email, setEmail] = useState("")
    const [registration, setRegistration] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Form d'inscription (payload react-page) chargé à la demande.
    const [regForm, setRegForm] = useState(null)
    const [regOrg, setRegOrg] = useState(null)
    const [regLoading, setRegLoading] = useState(false)
    const [regError, setRegError] = useState(null)

    const [codeForm] = Form.useForm()
    const activeLang = lang?.value || lang || org?.language || "fr"

    // Charge le formulaire d'inscription quand on bascule sur "signup".
    // Charge le formulaire d'inscription au 1er passage en "signup".
    // ⚠️ NE PAS mettre regLoading/regForm dans les deps : ça re-déclencherait
    // l'effect, dont le cleanup invaliderait le run porteur du fetch → spinner
    // infini. On garde l'unicité via une ref, et on ne dépend que de mode/identifier.
    const regRequested = React.useRef(false)
    useEffect(() => {
        if (mode !== "signup" || regRequested.current) return
        regRequested.current = true
        setRegLoading(true)
        setRegError(null)
        ;(async () => {
            try {
                const res = await AccountAPI.registrationForm(identifier)
                if (res?.success && res.form) {
                    setRegForm(res.form)
                    setRegOrg(res.org)
                } else {
                    setRegError(res?.error === "ACCOUNT_DISABLED"
                        ? t`L'espace compte n'est pas activé.`
                        : t`Impossible de charger le formulaire d'inscription.`)
                }
            } catch (_) {
                setRegError(t`Impossible de charger le formulaire d'inscription.`)
            } finally {
                setRegLoading(false)
            }
        })()
    }, [mode, identifier])

    // Login (email seul).
    async function onSendCodeLogin(values) {
        setError(null)
        setLoading(true)
        const cleanEmail = (values.email || "").trim().toLowerCase()
        try {
            await AccountAPI.requestCode(identifier, cleanEmail, activeLang)
        } catch (_) { /* réponse neutre */ }
        setEmail(cleanEmail)
        setRegistration(null)
        setStep("code")
        setLoading(false)
    }

    // Inscription : soumission du form dynamique (interceptée via onSubmitOverride).
    async function onRegistrationSubmit(data) {
        setError(null)
        const reg = buildRegistrationFromSubmit(data)
        const cleanEmail = (reg.email || "").trim().toLowerCase()
        if (!cleanEmail) {
            setRegError(t`Veuillez renseigner votre email.`)
            return
        }
        setLoading(true)
        try {
            await AccountAPI.requestCode(identifier, cleanEmail, activeLang)
        } catch (_) { /* réponse neutre */ }
        setEmail(cleanEmail)
        setRegistration(reg)
        setStep("code")
        setLoading(false)
    }

    async function onVerify(values) {
        setError(null)
        setLoading(true)
        try {
            const res = await AccountAPI.verifyCode(identifier, email, (values.code || "").trim(), registration, activeLang)
            // La session est désormais portée par un cookie HttpOnly posé par le BFF
            // (le token n'est plus renvoyé dans le corps). On se fie donc à `success`.
            if (res?.success) {
                setToken() // pose le hint « connecté » (le vrai token est dans le cookie)
                onAuthenticated(res)
            } else {
                setError(t`Code invalide ou expiré.`)
            }
        } catch (_) {
            setError(t`Code invalide ou expiré.`)
        } finally {
            setLoading(false)
        }
    }

    async function resendCode() {
        setError(null)
        try { await AccountAPI.requestCode(identifier, email, activeLang) } catch (_) {}
    }

    // ── Écran code (partagé login + signup) ──────────────────────────────
    if (step === "code") {
        return (
            <div style={{maxWidth: 420, margin: "0 auto"}}>
                <Header org={org}/>
                <Card>
                    <p style={{marginBottom: 16}}>
                        <Trans>Nous avons envoyé un code à</Trans>{" "}<strong>{email}</strong>.
                    </p>
                    {error && <Alert type="error" showIcon style={{marginBottom: 16}} message={error}/>}
                    <Form form={codeForm} layout="vertical" onFinish={onVerify}>
                        <Form.Item
                            name="code"
                            label={t`Code de connexion`}
                            rules={[{required: true, message: t`Saisissez le code reçu`}]}
                        >
                            <Input
                                inputMode="numeric"
                                maxLength={6}
                                placeholder="______"
                                style={{letterSpacing: 6, textAlign: "center", fontSize: 20}}
                            />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" block loading={loading}>
                            <Trans>Me connecter</Trans>
                        </Button>
                    </Form>
                    <div style={{display: "flex", justifyContent: "space-between", marginTop: 16}}>
                        <Button type="link" style={{padding: 0}} onClick={() => { setStep("form"); setError(null) }}>
                            <Trans>Retour</Trans>
                        </Button>
                        <Button type="link" style={{padding: 0}} onClick={resendCode}>
                            <Trans>Renvoyer le code</Trans>
                        </Button>
                    </div>
                </Card>
            </div>
        )
    }

    // ── Écran form (login OU signup) ─────────────────────────────────────
    return (
        <div style={{maxWidth: mode === "signup" ? 760 : 420, margin: "0 auto"}}>
            <Header org={org}/>

            <Segmented
                block
                value={mode}
                onChange={(v) => { setMode(v); setError(null) }}
                options={[
                    {label: t`Se connecter`, value: "login"},
                    {label: t`Créer un compte`, value: "signup"},
                ]}
                style={{marginBottom: 20, maxWidth: 420, marginLeft: "auto", marginRight: "auto", display: "block"}}
            />

            {mode === "login" && (
                <Card style={{maxWidth: 420, margin: "0 auto"}}>
                    {error && <Alert type="error" showIcon style={{marginBottom: 16}} message={error}/>}
                    <Form layout="vertical" onFinish={onSendCodeLogin}>
                        <Form.Item
                            name="email"
                            label={t`Email`}
                            rules={[
                                {required: true, message: t`Indiquez votre email`},
                                {type: "email", message: t`Email invalide`},
                            ]}
                        >
                            <Input autoComplete="email" placeholder="exemple@email.com"/>
                        </Form.Item>
                        <Button type="primary" htmlType="submit" block loading={loading}>
                            <Trans>Recevoir mon code</Trans>
                        </Button>
                    </Form>
                </Card>
            )}

            {mode === "signup" && (
                <Card styles={{body: {paddingTop: 8}}}>
                    {regLoading && <div style={{textAlign: "center", padding: 32}}><Spin/></div>}
                    {regError && <Alert type="error" showIcon style={{marginBottom: 16}} message={regError}/>}
                    {error && <Alert type="error" showIcon style={{marginBottom: 16}} message={error}/>}
                    {regForm && (
                        <AccountEditorRender
                            form={regForm}
                            org={regOrg || org}
                            lang={lang}
                            onData={onRegistrationSubmit}
                        />
                    )}
                </Card>
            )}
        </div>
    )
}

function Header({org}) {
    return (
        <div style={{textAlign: "center", marginBottom: 24}}>
            {org?.logo && <img src={org.logo} alt={org?.name || ""} style={{height: 56, marginBottom: 12}}/>}
            <h1 style={{fontSize: 22, margin: 0}}><Trans>Mon espace</Trans></h1>
            {org?.name && <Text type="secondary">{org.name}</Text>}
        </div>
    )
}
